import { fetchFile } from '@ffmpeg/util';
import {
    formatFileSize,
    getVideoDuration,
    isSupportedVideoFile,
    loadFFmpeg,
    preloadFFmpeg,
    resetFFmpeg,
} from '../compress/compressVideoEngine';

export {
    formatFileSize,
    getVideoDuration,
    isSupportedVideoFile,
    preloadFFmpeg,
};

export const MAX_TRIM_VIDEO_BYTES = 200 * 1024 * 1024;
export const MIN_TRIM_GAP_SECONDS = 0.2;

export function formatVideoTime(seconds) {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    const ms = Math.floor((seconds % 1) * 10);
    return `${m}:${String(s).padStart(2, '0')}.${ms}`;
}

export function trimmedVideoName(fileName) {
    const base = (fileName || 'video').replace(/\.[^.]+$/, '');
    return `${base}-trimmed.mp4`;
}

function toMp4Blob(data) {
    if (!data || data.length === 0) return null;
    const bytes = data instanceof Uint8Array ? data : new Uint8Array(data);
    return new Blob([bytes], { type: 'video/mp4' });
}

function trimInputName(file) {
    return `input${file.name.substring(file.name.lastIndexOf('.'))}`;
}

async function cleanupFiles(ffmpeg, ...names) {
    await Promise.all(names.map(async (name) => {
        try { await ffmpeg.deleteFile(name); } catch (_) { /* ignore */ }
    }));
}

/**
 * Same flow as sohozkaj-site videoProcessor.trimVideo — always writeFile, never WORKERFS.
 */
export async function trimVideo(file, startTime = 0, endTime = 0, onProgress = () => {}) {
    let ffmpeg;
    const inputName = trimInputName(file);
    const outputName = 'trimmed-output.mp4';

    try {
        onProgress(1);
        ffmpeg = await loadFFmpeg();
        if (!ffmpeg?.loaded) {
            throw new Error('FFMPEG_NOT_LOADED');
        }

        onProgress(5);
        const duration = await getVideoDuration(file);
        const safeStart = Math.max(0, Number(startTime) || 0);
        const safeEnd = endTime > 0
            ? Math.min(Number(endTime), duration || Number(endTime))
            : duration;
        const trimDuration = Math.max(0.1, safeEnd - safeStart);

        if (duration > 0 && safeStart >= safeEnd) {
            throw new Error('INVALID_RANGE');
        }

        await cleanupFiles(ffmpeg, inputName, outputName);
        onProgress(8);
        await ffmpeg.writeFile(inputName, await fetchFile(file));
        onProgress(15);

        const progressHandler = ({ progress }) => {
            const pct = Math.round(Math.min(progress * 100, 100));
            onProgress(Math.min(99, 15 + Math.round(pct * 0.84)));
        };
        ffmpeg.on('progress', progressHandler);

        const execTimeout = Math.max(180000, Math.ceil(trimDuration * 10000));
        const execArgs = [
            '-ss', String(safeStart),
            '-i', inputName,
            '-t', String(trimDuration),
        ];

        // Fast path: stream copy (reference site behaviour for quick trims).
        let exitCode = await ffmpeg.exec([
            ...execArgs,
            '-c', 'copy',
            '-avoid_negative_ts', 'make_zero',
            '-movflags', '+faststart',
            outputName,
        ], execTimeout);

        let data = null;
        if (exitCode === 0) {
            data = await ffmpeg.readFile(outputName);
            const blob = toMp4Blob(data);
            if (blob && blob.size > 1024) {
                ffmpeg.off('progress', progressHandler);
                onProgress(100);
                return blob;
            }
        }

        await cleanupFiles(ffmpeg, outputName);
        onProgress(50);

        // Fallback: re-encode (exact reference args).
        exitCode = await ffmpeg.exec([
            ...execArgs,
            '-vcodec', 'libx264',
            '-preset', 'ultrafast',
            '-crf', '18',
            '-acodec', 'aac',
            '-movflags', '+faststart',
            outputName,
        ], execTimeout);

        ffmpeg.off('progress', progressHandler);

        if (exitCode !== 0) {
            throw new Error(exitCode === 1 ? 'TIMEOUT' : 'ENCODE_FAILED');
        }

        onProgress(98);
        data = await ffmpeg.readFile(outputName);
        const blob = toMp4Blob(data);
        if (!blob || blob.size === 0) {
            throw new Error('EMPTY_OUTPUT');
        }

        onProgress(100);
        return blob;
    } catch (error) {
        if (error?.message === 'FFMPEG_NOT_LOADED') {
            resetFFmpeg();
        }
        const msg = (error?.message || '').toLowerCase();
        if (msg.includes('memory') || msg.includes('allocate') || msg.includes('oom')) {
            throw new Error('OUT_OF_MEMORY');
        }
        if (
            error?.message === 'INVALID_RANGE'
            || error?.message === 'ENCODE_FAILED'
            || error?.message === 'EMPTY_OUTPUT'
            || error?.message === 'TIMEOUT'
            || error?.message === 'FFMPEG_NOT_LOADED'
        ) {
            throw error;
        }
        throw new Error('TRIM_FAILED');
    } finally {
        if (ffmpeg) {
            await cleanupFiles(ffmpeg, inputName, outputName);
        }
    }
}
